import React, { useState } from "react";
import DataList from "components/datatable/DataList";
import PopupDialog from "components/dialog/PopupDialog";
import { useAuth } from "context/AuthProvider";
import { MyConst } from "data/MyConst";
import useAxios from "hooks/useAxios";
import useAxiosFunction from "hooks/useAxiosFunction";
import useMsgBox from "hooks/useMsgBox";
import { postgresDateToIndonesia, RuleRequired } from "utils/MyUtils";
import { showEmpty } from "features/kms/system/SystemPageUtils";
import { formatPersonName } from "features/kms/common/nameFormat";

function addSimpleReferenceConfig(title, url, cellid, label) {
  return {
    title,
    url,
    cellid,
    columns: ["nama"],
    coltype: {
      nama: {
        type: "text",
        label,
        name: "nama",
        validation: RuleRequired,
        colsize: 12,
      },
    },
    optionsUrl: url,
    optionsAuth: true,
    otherLabel: "Lainnya",
  };
}

function attachmentLabel(value) {
  if (!value) return "-";
  return "Lihat";
}

function capaianAssetUrl(filename) {
  const clean = String(filename || "").trim();
  if (!clean) return "";
  return `${String(MyConst.URLASET || "").replace(/\/$/, "")}/capaian/${clean}`;
}

function formatDate(value) {
  return value ? postgresDateToIndonesia(value) || value : "";
}

function joinedLines(...values) {
  return values.filter(Boolean).join("\n") || "-";
}

function renderLineBreak(value) {
  return <span style={{ whiteSpace: "pre-line" }}>{showEmpty(value, "-")}</span>;
}

function renderNamaPeran(_val, _col, item) {
  const formattedName = formatPersonName(item, ["nama_lengkap"]);
  const name = formattedName ? `${formattedName} (${showEmpty(item?.peran_name, "-")})` : "";
  return renderLineBreak(joinedLines(name, item?.nomor_induk));
}

function exportNamaPeran(_val, _col, item) {
  const formattedName = formatPersonName(item, ["nama_lengkap"]);
  const name = formattedName ? `${formattedName} (${showEmpty(item?.peran_name, "-")})` : "";
  return joinedLines(name, item?.nomor_induk);
}

function renderStatusWithReview(_val, _col, item) {
  const status = showEmpty(item?.status_name, "-");
  const note = String(item?.catatan_perbaikan || "").trim();
  if (String(item?.status_code || "").toUpperCase() !== "PERLU_PERBAIKAN" || !note) {
    return status;
  }
  return renderLineBreak(`${status}\nCatatan: ${note}`);
}

function exportStatusWithReview(_val, _col, item) {
  const status = showEmpty(item?.status_name, "-");
  const note = String(item?.catatan_perbaikan || "").trim();
  if (String(item?.status_code || "").toUpperCase() !== "PERLU_PERBAIKAN" || !note) {
    return status;
  }
  return `${status}\nCatatan: ${note}`;
}

function ReviewNotePopup({
  show,
  title,
  itemName,
  note,
  setNote,
  onCancel,
  onSubmit,
  loading = false,
}) {
  return (
    <PopupDialog
      title={title}
      size="md"
      show={show}
      closeHandle={onCancel}
      allowClose
      closeLabel="Batal"
      doHandle={onSubmit}
      doLabel={loading ? "Menyimpan..." : "Simpan"}
      doDisabled={loading}
    >
      <div className="form-group mb-0">
        <label>Catatan Perbaikan</label>
        <textarea
          className="form-control"
          rows={4}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder={`Tuliskan catatan perbaikan untuk ${itemName || "data ini"}`}
        />
        <small className="form-text text-muted">
          Catatan ini akan ditampilkan kepada pengguna sebagai panduan perbaikan data.
        </small>
      </div>
    </PopupDialog>
  );
}

const statusOptions = [
  { id: "DRAFT", label: "Draft" },
  { id: "DIAJUKAN", label: "Diajukan" },
  { id: "PERLU_PERBAIKAN", label: "Perlu Perbaikan" },
  { id: "TERVERIFIKASI", label: "Terverifikasi" },
];

export function PersonalCertificationTable({ baseUrl = "/profile/sertifikasi", peranOptionsUrl = "/profile/capaian/peran-options", reviewQueue = false }) {
  const { user } = useAuth();
  const [, , , axiosFetch] = useAxiosFunction();
  const [showMsg, confirmMsg] = useMsgBox();
  const [reloadKey, setReloadKey] = useState(0);
  const [revisionTarget, setRevisionTarget] = useState(null);
  const [revisionNote, setRevisionNote] = useState("");
  const normalizedBaseUrl = String(baseUrl || "/profile/sertifikasi").replace(/\/$/, "");
  const [peranResp] = useAxios({ method: "GET", url: peranOptionsUrl, auth: true });
  const [jenisResp] = useAxios({ method: "GET", url: "/references/jenis-sertifikasi", auth: true });
  const [tingkatResp] = useAxios({ method: "GET", url: "/references/tingkat-sertifikasi", auth: true });
  const peranOptions = Array.isArray(peranResp?.data) ? peranResp.data : [];
  const jenisOptions = Array.isArray(jenisResp?.data) ? jenisResp.data : [];
  const tingkatOptions = Array.isArray(tingkatResp?.data) ? tingkatResp.data : [];
  const groupName = String(user?.groupname || "").toUpperCase();
  const canVerify = ["SUPERADMIN", "ADMIN_KONI_PROVINSI", "ADMIN_KONI_KABKOTA", "ADMIN_CABOR"].includes(groupName);
  const canEditCertification = (item) => {
    const status = String(item?.status_code || "").toUpperCase();
    if (canVerify) {
      return status === "DIAJUKAN" || status === "PERLU_PERBAIKAN";
    }
    if (["PERSONAL", "ADMIN_KLUB"].includes(groupName)) {
      return status === "PERLU_PERBAIKAN";
    }
    return false;
  };
  const canDeleteCertification = (item) => {
    const status = String(item?.status_code || "").toUpperCase();
    return canVerify && (status === "DIAJUKAN" || status === "PERLU_PERBAIKAN");
  };

  const coltype = {
    id_personal_sertifikasi: { type: "hidden", name: "id_personal_sertifikasi" },
    id_peran_keanggotaan: {
      label: "Sertifikasi dalam Peran",
      type: "dropdown",
      name: "id_peran_keanggotaan",
      colsize: 6,
      options: peranOptions,
      valueId: "id_peran_keanggotaan",
      labelId: "nama",
      placeholder: "-Pilih Peran-",
      validation: RuleRequired,
    },
    peran_name: { label: "Peran", renderFunc: (val) => showEmpty(val), width: 70 },
    nama_peran: {
      label: "Nama",
      renderFunc: renderNamaPeran,
      renderExportFunc: exportNamaPeran,
      width: 180,
    },
    nama_sertifikasi: {
      label: "Nama Sertifikasi",
      type: "text",
      name: "nama_sertifikasi",
      colsize: 12,
      validation: RuleRequired,
      renderFunc: (val) => showEmpty(val),
      width: 150,
    },
    id_jenis_sertifikasi: {
      label: "Jenis Sertifikasi",
      type: "dropdowndinamis",
      name: "id_jenis_sertifikasi",
      colsize: 6,
      options: jenisOptions,
      valueId: "id_jenis_sertifikasi",
      labelId: "nama",
      placeholder: "-Pilih Jenis Sertifikasi-",
      validation: RuleRequired,
      add_config: addSimpleReferenceConfig("Tambah Jenis Sertifikasi", "/references/jenis-sertifikasi", "id_jenis_sertifikasi", "Nama Jenis Sertifikasi"),
    },
    jenis_sertifikasi_name: { label: "Jenis", renderFunc: (val) => showEmpty(val), width: 130 },
    jenis_tingkat: {
      label: "Jenis",
      renderFunc: (_val, _col, item) => renderLineBreak(joinedLines(
        item?.jenis_sertifikasi_name ? `${item.jenis_sertifikasi_name},` : "",
        item?.tingkat_name ? `Tk. ${item.tingkat_name}` : "",
      )),
      renderExportFunc: (_val, _col, item) => joinedLines(
        item?.jenis_sertifikasi_name ? `${item.jenis_sertifikasi_name},` : "",
        item?.tingkat_name ? `Tk. ${item.tingkat_name}` : "",
      ),
      width: 150,
    },
    id_tingkat_capaian: {
      label: "Tingkat",
      type: "dropdown",
      name: "id_tingkat_capaian",
      colsize: 6,
      options: tingkatOptions,
      valueId: "id_tingkat_capaian",
      labelId: "nama",
      placeholder: "-Pilih Tingkat-",
      validation: RuleRequired,
    },
    tingkat_name: { label: "Tingkat", renderFunc: (val) => showEmpty(val), width: 130 },
    lembaga_penyelenggara: {
      label: "Lembaga Penyelenggara",
      type: "text",
      name: "lembaga_penyelenggara",
      colsize: 6,
      validation: RuleRequired,
      renderFunc: (val) => showEmpty(val),
      width: 155,
    },
    nomor: {
      label: "Nomor Sertifikat",
      type: "text",
      name: "nomor",
      colsize: 6,
      validation: RuleRequired,
      renderFunc: (val) => showEmpty(val),
      width: 130,
    },
    tanggal_sertifikat: {
      label: "Tanggal Sertifikat",
      type: "date",
      name: "tanggal_sertifikat",
      colsize: 4,
      validation: RuleRequired,
      width: 140,
      align: "center",
    },
    sertifikat: {
      label: "Sertifikat",
      renderFunc: (_val, _col, item) => {
        const text = joinedLines(
          item?.nomor ? `No. ${item.nomor}` : "",
          item?.tanggal_sertifikat ? `Tgl. ${formatDate(item.tanggal_sertifikat)}` : "",
        );
        const url = capaianAssetUrl(item?.file_sertifikat);
        return url ? (
          <a href={url} target="_blank" rel="noreferrer" style={{ whiteSpace: "pre-line" }}>
            {text}
          </a>
        ) : renderLineBreak(text);
      },
      renderExportFunc: (_val, _col, item, _coltype, exportMode) => {
        const label = joinedLines(
          item?.nomor ? `No. ${item.nomor}` : "",
          item?.tanggal_sertifikat ? `Tgl. ${formatDate(item.tanggal_sertifikat)}` : "",
        );
        const url = capaianAssetUrl(item?.file_sertifikat);
        if (url && exportMode !== "pdf") {
          return { __exportType: "link", href: url, label };
        }
        return label;
      },
      width: 150,
    },
    masa_berlaku: {
      label: "Masa Berlaku",
      renderFunc: (_val, _col, item) => showEmpty(
        [formatDate(item?.berlaku_dari), formatDate(item?.berlaku_sampai)].filter(Boolean).join(" - "),
      ),
      renderExportFunc: (_val, _col, item) => [formatDate(item?.berlaku_dari), formatDate(item?.berlaku_sampai)].filter(Boolean).join(" - "),
      width: 120,
    },
    berlaku_dari: { label: "Berlaku Dari", type: "date", name: "berlaku_dari", colsize: 4, width: 110, align: "center" },
    berlaku_sampai: { label: "Berlaku Sampai", type: "date", name: "berlaku_sampai", colsize: 4, width: 120, align: "center" },
    file_sertifikat: {
      label: "File Sertifikat",
      type: "filedrop",
      name: "file_sertifikat",
      colsize: 12,
      url: "/file/upload",
      dropname: "sertifikat",
      data: { location: "capaian", name: "sertifikat" },
      validation: RuleRequired,
      renderFunc: attachmentLabel,
      width: 110,
      align: "center",
    },
    status_name: { label: "Status", renderFunc: renderStatusWithReview, renderExportFunc: exportStatusWithReview, width: 80, align: "center" },
    action: { label: "Aksi", align: "center", width: canVerify ? 70 : 50 },
  };

  const formColumns = [
    "id_peran_keanggotaan",
    "nama_sertifikasi",
    "id_jenis_sertifikasi",
    "id_tingkat_capaian",
    "lembaga_penyelenggara",
    "nomor",
    "tanggal_sertifikat",
    "berlaku_dari",
    "berlaku_sampai",
    "file_sertifikat",
  ];

  const saveHandle = async (form, defaultValue = {}) => {
    const id = form?.id_personal_sertifikasi || defaultValue?.id_personal_sertifikasi || "";
    return axiosFetch({
      method: id ? "PUT" : "POST",
      url: id ? `${normalizedBaseUrl}/${id}` : normalizedBaseUrl,
      auth: true,
      data: {
        id_peran_keanggotaan: Number(form?.id_peran_keanggotaan || 0),
        nama_sertifikasi: form?.nama_sertifikasi || "",
        id_jenis_sertifikasi: Number(form?.id_jenis_sertifikasi || 0),
        id_tingkat_capaian: Number(form?.id_tingkat_capaian || 0),
        lembaga_penyelenggara: form?.lembaga_penyelenggara || "",
        nomor: form?.nomor || "",
        tanggal_sertifikat: form?.tanggal_sertifikat || "",
        berlaku_dari: form?.berlaku_dari || "",
        berlaku_sampai: form?.berlaku_sampai || "",
        file_sertifikat: form?.file_sertifikat || "",
      },
    });
  };

  const changeStatusHandle = async (item, statusCode, catatan = "") => {
    const label = statusCode === "TERVERIFIKASI" ? "memverifikasi" : "menandai perlu perbaikan";
    if (statusCode === "PERLU_PERBAIKAN" && !String(catatan || "").trim()) {
      await showMsg("Informasi", "Catatan Perbaikan wajib diisi.");
      return;
    }
    if (statusCode !== "PERLU_PERBAIKAN") {
      const ok = await confirmMsg(
        "Konfirmasi",
        `Apakah Anda yakin akan ${label} sertifikasi ${item?.nama_sertifikasi || "ini"}?`,
      );
      if (!ok) return;
    }

    const result = await axiosFetch({
      method: "PUT",
      url: `${normalizedBaseUrl}/${item?.id_personal_sertifikasi}/status`,
      auth: true,
      data: { status_code: statusCode, catatan },
    });
    if (result?.data?.success === true) {
      await showMsg("Informasi", "Status Sertifikasi berhasil disimpan.");
      setRevisionTarget(null);
      setRevisionNote("");
      setReloadKey((current) => current + 1);
      return;
    }
    await showMsg("Error", result?.message || "Status Sertifikasi gagal disimpan.");
  };

  const renderCertificationAction = (_val, _col, item) => {
    if (!canVerify) return null;
    const status = String(item?.status_code || "").toUpperCase();
    const verificationButton = status === "DIAJUKAN" ? (
      <>
        <a
          href="#"
          onClick={(event) => {
            event.preventDefault();
            changeStatusHandle(item, "TERVERIFIKASI");
          }}
          className="btn btn-success btn-xs"
          title="Verifikasi"
        >
          <i className="fa fa-check-circle"></i>
        </a>
        &nbsp;
      </>
    ) : null;
    const revisionButton = status === "DIAJUKAN" || status === "TERVERIFIKASI" ? (
      <>
        <a
          href="#"
          onClick={(event) => {
            event.preventDefault();
            setRevisionTarget(item);
            setRevisionNote("");
          }}
          className="btn btn-warning btn-xs"
          title="Perbaiki"
        >
          <i className="fa fa-tools"></i>
        </a>
        &nbsp;
      </>
    ) : null;
    if (verificationButton || revisionButton) {
      return (
        <>
          {verificationButton}
          {revisionButton}
        </>
      );
    }
    return null;
  };

  return (
    <>
      <DataList
        title="Sertifikasi"
        coltype={coltype}
        dlist={{
          url: normalizedBaseUrl,
          render_action_extension: renderCertificationAction,
          columns: ["no", reviewQueue ? "nama_peran" : "peran_name", "nama_sertifikasi", "jenis_tingkat", "lembaga_penyelenggara", "sertifikat", "masa_berlaku", "status_name", "action"],
          filter: [
            { dtype: "q", name: "q", colsize: 3 },
            {
              dtype: "dropdown",
              name: "id_peran_keanggotaan",
              label: "Peran",
              colsize: 3,
              options: peranOptions,
              valueId: "id_peran_keanggotaan",
              labelId: "nama",
              placeholder: "-Semua Peran-",
            },
            {
              dtype: "dropdown",
              name: "id_jenis_sertifikasi",
              label: "Jenis Sertifikasi",
              colsize: 3,
              options: jenisOptions,
              valueId: "id_jenis_sertifikasi",
              labelId: "nama",
              placeholder: "-Semua Jenis-",
            },
            {
              dtype: "dropdown",
              name: "status_code",
              label: "Status",
              colsize: 3,
              options: statusOptions,
              valueId: "id",
              labelId: "label",
              placeholder: "-Semua Status-",
            },
          ],
          sort_by: [reviewQueue ? "nama_lengkap" : "peran_name", "nama_sertifikasi", "jenis_sertifikasi_name", "tingkat_name", "tanggal_sertifikat", "status_name"],
        }}
        reload={reloadKey}
        addconfig={reviewQueue ? null : { columns: formColumns, title: "Tambah Sertifikasi", label_tool: "Tambah Sertifikasi", saveHandle }}
        editconfig={{ columns: ["id_personal_sertifikasi", ...formColumns], title: "Ubah Sertifikasi", url: normalizedBaseUrl, urlitem: normalizedBaseUrl, cellid: "id_personal_sertifikasi", saveHandle, isAllow: canEditCertification }}
        delconfig={{ url: normalizedBaseUrl, cellid: "id_personal_sertifikasi", cellname: "nama_sertifikasi", isAllow: canDeleteCertification }}
        pdflist={{ title: "Sertifikasi", orientation: "landscape" }}
        xlslist={{ title: "Sertifikasi" }}
      />
      <ReviewNotePopup
        show={Boolean(revisionTarget)}
        title="Catatan Perbaikan Sertifikasi"
        itemName={revisionTarget?.nama_sertifikasi}
        note={revisionNote}
        setNote={setRevisionNote}
        onCancel={() => {
          setRevisionTarget(null);
          setRevisionNote("");
        }}
        onSubmit={() => changeStatusHandle(revisionTarget, "PERLU_PERBAIKAN", revisionNote)}
      />
    </>
  );
}

export function PersonalAchievementTable({ baseUrl = "/profile/prestasi", optionsBaseUrl = "/profile/capaian", peranOptionsUrl = "", reviewQueue = false }) {
  const { user } = useAuth();
  const [, , , axiosFetch] = useAxiosFunction();
  const [showMsg, confirmMsg] = useMsgBox();
  const [selectedCaborId, setSelectedCaborId] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [revisionTarget, setRevisionTarget] = useState(null);
  const [revisionNote, setRevisionNote] = useState("");
  const normalizedBaseUrl = String(baseUrl || "/profile/prestasi").replace(/\/$/, "");
  const normalizedOptionsBaseUrl = String(optionsBaseUrl || "/profile/capaian").replace(/\/$/, "");
  const normalizedPeranOptionsUrl = String(peranOptionsUrl || `${normalizedOptionsBaseUrl}/peran-options`).replace(/\/$/, "");
  const [peranResp] = useAxios({ method: "GET", url: normalizedPeranOptionsUrl, auth: true });
  const [caborResp] = useAxios({ method: "GET", url: `${normalizedOptionsBaseUrl}/cabor-options`, auth: true });
  const nomorOptionsUrl = selectedCaborId
    ? `${normalizedOptionsBaseUrl}/nomor-options?id_cabor=${encodeURIComponent(selectedCaborId)}`
    : `${normalizedOptionsBaseUrl}/nomor-options`;
  const [nomorResp] = useAxios({ method: "GET", url: nomorOptionsUrl, auth: true });
  const [hasilResp] = useAxios({ method: "GET", url: "/references/hasil-prestasi", auth: true });
  const [buktiResp] = useAxios({ method: "GET", url: "/references/bukti-prestasi", auth: true });
  const peranOptions = Array.isArray(peranResp?.data) ? peranResp.data : [];
  const caborOptions = Array.isArray(caborResp?.data) ? caborResp.data : [];
  const nomorOptions = Array.isArray(nomorResp?.data) ? nomorResp.data : [];
  const hasilOptions = Array.isArray(hasilResp?.data)
    ? hasilResp.data.map((item) => ({ ...item, label_hasil: `${item?.tingkat_name || ""} - ${item?.nama || ""} (${item?.skor || 0})` }))
    : [];
  const buktiOptions = Array.isArray(buktiResp?.data) ? buktiResp.data : [];
  const groupName = String(user?.groupname || "").toUpperCase();
  const canVerify = ["SUPERADMIN", "ADMIN_KONI_PROVINSI", "ADMIN_KONI_KABKOTA", "ADMIN_CABOR"].includes(groupName);
  const canEditAchievement = (item) => {
    const status = String(item?.status_code || "").toUpperCase();
    if (canVerify) {
      return status === "DIAJUKAN" || status === "PERLU_PERBAIKAN";
    }
    if (["PERSONAL", "ADMIN_KLUB"].includes(groupName)) {
      return status === "PERLU_PERBAIKAN";
    }
    return false;
  };
  const canDeleteAchievement = (item) => {
    const status = String(item?.status_code || "").toUpperCase();
    return canVerify && (status === "DIAJUKAN" || status === "PERLU_PERBAIKAN");
  };

  const coltype = {
    id_personal_prestasi: { type: "hidden", name: "id_personal_prestasi" },
    id_peran_keanggotaan: {
      label: "Prestasi sebagai",
      type: "dropdown",
      name: "id_peran_keanggotaan",
      colsize: 6,
      options: peranOptions,
      valueId: "id_peran_keanggotaan",
      labelId: "nama",
      placeholder: "-Pilih Peran-",
      validation: RuleRequired,
    },
    peran_name: { label: "Peran", renderFunc: (val) => showEmpty(val), width: 70 },
    nama_peran: {
      label: "Nama",
      renderFunc: renderNamaPeran,
      renderExportFunc: exportNamaPeran,
      width: 180,
    },
    nama_pertandingan: {
      label: "Nama Pertandingan",
      type: "text",
      name: "nama_pertandingan",
      colsize: 12,
      validation: RuleRequired,
      renderFunc: (val) => showEmpty(val),
      width: 150,
    },
    id_cabor: {
      label: "Cabor",
      type: "dropdown",
      name: "id_cabor",
      colsize: 6,
      options: caborOptions,
      valueId: "id_cabor",
      labelId: "nama",
      placeholder: "-Pilih Cabor-",
      validation: RuleRequired,
    },
    cabor_name: { label: "Cabor", renderFunc: (val) => showEmpty(val), width: 160 },
    cabor_nomor: {
      label: "Nomor Pertandingan",
      renderFunc: (_val, _col, item) => renderLineBreak(joinedLines(
        [item?.cabor_name, item?.nomor_pertandingan_name].filter(Boolean).join(" - "),
      )),
      renderExportFunc: (_val, _col, item) => [item?.cabor_name, item?.nomor_pertandingan_name].filter(Boolean).join(" - ") || "-",
      width: 190,
    },
    id_nomor_pertandingan: {
      label: "Nomor Pertandingan",
      type: "dropdown",
      name: "id_nomor_pertandingan",
      colsize: 6,
      options: nomorOptions,
      valueId: "id_nomor_pertandingan",
      labelId: "nama",
      placeholder: "-Pilih Nomor Pertandingan-",
      validation: RuleRequired,
    },
    nomor_pertandingan_name: { label: "Nomor Pertandingan", renderFunc: (val) => showEmpty(val), width: 180 },
    id_hasil_prestasi: {
      label: "Hasil Pertandingan",
      type: "dropdown",
      name: "id_hasil_prestasi",
      colsize: 6,
      options: hasilOptions,
      valueId: "id_hasil_prestasi",
      labelId: "label_hasil",
      placeholder: "-Pilih Hasil Pertandingan-",
      validation: RuleRequired,
    },
    hasil_name: { label: "Hasil", renderFunc: (val) => showEmpty(val), width: 150 },
    prestasi_summary: {
      label: "Prestasi",
      renderFunc: (_val, _col, item) => renderLineBreak(joinedLines(
        item?.hasil_name ? `${item.hasil_name} (poin: ${showEmpty(item?.skor, "0")})` : "",
        item?.catatan_skor ? `Catatan: ${item.catatan_skor}` : "",
      )),
      renderExportFunc: (_val, _col, item) => joinedLines(
        item?.hasil_name ? `${item.hasil_name} (poin: ${showEmpty(item?.skor, "0")})` : "",
        item?.catatan_skor ? `Catatan: ${item.catatan_skor}` : "",
      ),
      width: 165,
    },
    tingkat_name: { label: "Tingkat", renderFunc: (val) => showEmpty(val), width: 105 },
    skor: { label: "Skor", width: 70, align: "center" },
    tempat_pertandingan: {
      label: "Tempat Pertandingan",
      type: "text",
      name: "tempat_pertandingan",
      colsize: 6,
      validation: RuleRequired,
      renderFunc: (val) => showEmpty(val),
      width: 155,
    },
    tanggal_awal: { label: "Tanggal Awal", type: "date", name: "tanggal_awal", colsize: 6, validation: RuleRequired, width: 110, align: "center" },
    tempat_tanggal: {
      label: "Tempat dan Tanggal",
      renderFunc: (_val, _col, item) => {
        const dateRange = [formatDate(item?.tanggal_awal), formatDate(item?.tanggal_akhir)].filter(Boolean).join(" - ");
        return renderLineBreak([item?.tempat_pertandingan, dateRange].filter(Boolean).join(", "));
      },
      renderExportFunc: (_val, _col, item) => {
        const dateRange = [formatDate(item?.tanggal_awal), formatDate(item?.tanggal_akhir)].filter(Boolean).join(" - ");
        return [item?.tempat_pertandingan, dateRange].filter(Boolean).join(", ") || "-";
      },
      width: 160,
    },
    tanggal_akhir: { label: "Tanggal Akhir", type: "date", name: "tanggal_akhir", colsize: 6, validation: RuleRequired, width: 110, align: "center" },
    catatan_skor: { label: "Catatan Skor/Waktu", type: "textarea", name: "catatan_skor", colsize: 6, validation: RuleRequired, renderFunc: (val) => showEmpty(val), width: 160 },
    id_bukti_prestasi: {
      label: "Bukti",
      type: "dropdown",
      name: "id_bukti_prestasi",
      colsize: 6,
      options: buktiOptions,
      valueId: "id_bukti_prestasi",
      labelId: "nama",
      placeholder: "-Pilih Bukti-",
      validation: RuleRequired,
    },
    bukti_name: { label: "Bukti", renderFunc: (val) => showEmpty(val), width: 110 },
    file_bukti: {
      label: "File/Scan Bukti",
      type: "filedrop",
      name: "file_bukti",
      colsize: 12,
      url: "/file/upload",
      dropname: "bukti",
      data: { location: "capaian", name: "bukti" },
      validation: RuleRequired,
      renderFunc: attachmentLabel,
      width: 110,
      align: "center",
    },
    status_name: { label: "Status", renderFunc: renderStatusWithReview, renderExportFunc: exportStatusWithReview, width: 80, align: "center" },
    action: { label: "Aksi", align: "center", width: canVerify ? 70 : 50 },
  };

  const formColumns = [
    "id_peran_keanggotaan",
    "nama_pertandingan",
    "id_cabor",
    "id_nomor_pertandingan",
    "id_hasil_prestasi",
    "tempat_pertandingan",
    "tanggal_awal",
    "tanggal_akhir",
    "catatan_skor",
    "id_bukti_prestasi",
    "file_bukti",
  ];

  const saveHandle = async (form, defaultValue = {}) => {
    const id = form?.id_personal_prestasi || defaultValue?.id_personal_prestasi || "";
    return axiosFetch({
      method: id ? "PUT" : "POST",
      url: id ? `${normalizedBaseUrl}/${id}` : normalizedBaseUrl,
      auth: true,
      data: {
        id_peran_keanggotaan: Number(form?.id_peran_keanggotaan || 0),
        nama_pertandingan: form?.nama_pertandingan || "",
        id_cabor: form?.id_cabor || "",
        id_nomor_pertandingan: form?.id_nomor_pertandingan || "",
        id_hasil_prestasi: Number(form?.id_hasil_prestasi || 0),
        tempat_pertandingan: form?.tempat_pertandingan || "",
        tanggal_awal: form?.tanggal_awal || "",
        tanggal_akhir: form?.tanggal_akhir || "",
        catatan_skor: form?.catatan_skor || "",
        id_bukti_prestasi: Number(form?.id_bukti_prestasi || 0),
        file_bukti: form?.file_bukti || "",
      },
    });
  };

  const onChangeForm = (setValue, form) => {
    const nextCaborId = String(form?.id_cabor || "").trim();
    if (nextCaborId !== selectedCaborId) {
      setSelectedCaborId(nextCaborId);
      const isEditForm = Boolean(form?.id_personal_prestasi);
      const currentNomorId = String(form?.id_nomor_pertandingan || "").trim();
      if (!isEditForm || currentNomorId === "") {
        setValue?.("id_nomor_pertandingan", "");
      }
    }
  };

  const changeStatusHandle = async (item, statusCode, catatan = "") => {
    const label = statusCode === "TERVERIFIKASI" ? "memverifikasi" : "menandai perlu perbaikan";
    if (statusCode === "PERLU_PERBAIKAN" && !String(catatan || "").trim()) {
      await showMsg("Informasi", "Catatan Perbaikan wajib diisi.");
      return;
    }
    if (statusCode !== "PERLU_PERBAIKAN") {
      const ok = await confirmMsg(
        "Konfirmasi",
        `Apakah Anda yakin akan ${label} prestasi ${item?.nama_pertandingan || "ini"}?`,
      );
      if (!ok) return;
    }

    const result = await axiosFetch({
      method: "PUT",
      url: `${normalizedBaseUrl}/${item?.id_personal_prestasi}/status`,
      auth: true,
      data: { status_code: statusCode, catatan },
    });
    if (result?.data?.success === true) {
      await showMsg("Informasi", "Status Prestasi berhasil disimpan.");
      setRevisionTarget(null);
      setRevisionNote("");
      setReloadKey((current) => current + 1);
      return;
    }
    await showMsg("Error", result?.message || "Status Prestasi gagal disimpan.");
  };

  const renderAchievementAction = (_val, _col, item) => {
    if (!canVerify) return null;
    const status = String(item?.status_code || "").toUpperCase();
    const verificationButton = status === "DIAJUKAN" ? (
      <>
        <a
          href="#"
          onClick={(event) => {
            event.preventDefault();
            changeStatusHandle(item, "TERVERIFIKASI");
          }}
          className="btn btn-success btn-xs"
          title="Verifikasi"
        >
          <i className="fa fa-check-circle"></i>
        </a>
        &nbsp;
      </>
    ) : null;
    const revisionButton = status === "DIAJUKAN" || status === "TERVERIFIKASI" ? (
      <>
        <a
          href="#"
          onClick={(event) => {
            event.preventDefault();
            setRevisionTarget(item);
            setRevisionNote("");
          }}
          className="btn btn-warning btn-xs"
          title="Perbaiki"
        >
          <i className="fa fa-tools"></i>
        </a>
        &nbsp;
      </>
    ) : null;
    if (verificationButton || revisionButton) {
      return (
        <>
          {verificationButton}
          {revisionButton}
        </>
      );
    }
    return null;
  };

  return (
    <>
      <DataList
        title="Prestasi"
        coltype={coltype}
        dlist={{
          url: normalizedBaseUrl,
          render_action_extension: renderAchievementAction,
          columns: ["no", reviewQueue ? "nama_peran" : "peran_name", "nama_pertandingan", "cabor_nomor", "tingkat_name", "prestasi_summary", "tempat_tanggal", "status_name", "action"],
          filter: [
            { dtype: "q", name: "q", colsize: 3 },
            {
              dtype: "dropdown",
              name: "id_peran_keanggotaan",
              label: "Peran",
              colsize: 3,
              options: peranOptions,
              valueId: "id_peran_keanggotaan",
              labelId: "nama",
              placeholder: "-Semua Peran-",
            },
            {
              dtype: "dropdown",
              name: "id_cabor",
              label: "Cabor",
              colsize: 3,
              options: caborOptions,
              valueId: "id_cabor",
              labelId: "nama",
              placeholder: "-Semua Cabor-",
            },
            {
              dtype: "dropdown",
              name: "status_code",
              label: "Status",
              colsize: 3,
              options: statusOptions,
              valueId: "id",
              labelId: "label",
              placeholder: "-Semua Status-",
            },
          ],
          sort_by: [reviewQueue ? "nama_lengkap" : "peran_name", "nama_pertandingan", "cabor_name", "nomor_pertandingan_name", "tingkat_name", "hasil_name", "tanggal_awal", "status_name"],
        }}
        reload={reloadKey}
        addconfig={reviewQueue ? null : { columns: formColumns, title: "Tambah Prestasi", label_tool: "Tambah Prestasi", saveHandle, onChangeForm }}
        editconfig={{ columns: ["id_personal_prestasi", ...formColumns], title: "Ubah Prestasi", url: normalizedBaseUrl, urlitem: normalizedBaseUrl, cellid: "id_personal_prestasi", saveHandle, onChangeForm, isAllow: canEditAchievement }}
        delconfig={{ url: normalizedBaseUrl, cellid: "id_personal_prestasi", cellname: "nama_pertandingan", isAllow: canDeleteAchievement }}
        pdflist={{ title: "Prestasi", orientation: "landscape" }}
        xlslist={{ title: "Prestasi" }}
      />
      <ReviewNotePopup
        show={Boolean(revisionTarget)}
        title="Catatan Perbaikan Prestasi"
        itemName={revisionTarget?.nama_pertandingan}
        note={revisionNote}
        setNote={setRevisionNote}
        onCancel={() => {
          setRevisionTarget(null);
          setRevisionNote("");
        }}
        onSubmit={() => changeStatusHandle(revisionTarget, "PERLU_PERBAIKAN", revisionNote)}
      />
    </>
  );
}

export function PersonalCertificationPage() {
  return <PersonalCertificationTable />;
}

export function PersonalAchievementPage() {
  return <PersonalAchievementTable />;
}

export function VerificationAchievementSubmissionPage() {
  return <PersonalAchievementTable baseUrl="/ajuan-verifikasi/prestasi" optionsBaseUrl="/ajuan-verifikasi/capaian" reviewQueue />;
}

export function VerificationCertificationSubmissionPage() {
  return <PersonalCertificationTable baseUrl="/ajuan-verifikasi/sertifikasi" peranOptionsUrl="/ajuan-verifikasi/capaian/peran-options" reviewQueue />;
}
